// 퀘스트 스키마의 첫 데이터(정본: docs/CONTENT-90D-PRD.md 2.4). 지역 스토리 골격 + 이동 퀘스트 + 일일 3·주간 3. 대사·보상 수치는 c-quest 사이클에서 채운다.
import type { QuestDef, RegionNo, StageId, TownNo } from './types';
import { REGIONS_90D } from './world';

/** 지역마다: 중간보스(n-5)·지역보스(n-10) 스토리 2 + 다음 마을 이동 1(지역6은 엔딩이라 이동 없음) */
function storyFor(no: RegionNo): QuestDef[] {
  const r = REGIONS_90D[no - 1];
  const town = no as TownNo;
  const mid: QuestDef = { id: `story-r${no}-mid`, giver: 'chief', kind: 'story', town, name: `${r.midBoss} 토벌`, goals: [{ type: 'clearStage', stage: r.stages[4].id }], rewards: { gold: 0 }, after: no > 1 ? `travel-r${no - 1}` : undefined };
  const fin: QuestDef = { id: `story-r${no}-final`, giver: 'chief', kind: 'story', town, name: `${r.finalBoss} 격파`, goals: [{ type: 'clearStage', stage: r.stages[9].id }], rewards: { gold: 0 }, after: mid.id };
  if (no === 6) return [mid, fin];
  const travel: QuestDef = { id: `travel-r${no}`, giver: 'chief', kind: 'travel', town, name: `${REGIONS_90D[no].name} 방면으로`, goals: [{ type: 'reachTown', town: r.nextTown }], rewards: { gold: 0 }, after: fin.id };
  return [mid, fin, travel];
}

export const STORY_QUESTS: readonly QuestDef[] = ([1, 2, 3, 4, 5, 6] as const).flatMap(storyFor);

/** 일일 의뢰 3개(약 10분, PRD 2.4). 골드는 지역1 기준(× 지역 골드 배율), 합은 economy.ts ROUTINE.dailyQuestGold */
export const DAILY_QUESTS: readonly QuestDef[] = [
  // 현상금(PRD 2.2-3): 모험 탭의 현상금 보스 2마리 처치. 옛 사냥(500)+정찰(400) 골드 합 900을 그대로 옮겨 경제 합계는 같다
  { id: 'daily-bounty', giver: 'board', kind: 'daily', town: 1, name: '오늘의 현상금', goals: [{ type: 'bounty', count: 2 }], rewards: { gold: 900 } },
  { id: 'daily-enhance', giver: 'forge', kind: 'daily', town: 1, name: '대장간 단련', goals: [{ type: 'enhance', count: 1 }], rewards: { gold: 300, stones: 3 } },
];
/** 주간 3개: 보호 주문서 공급 3장(items.ts PROTECT_WEEKLY.quest) */
export const WEEKLY_QUESTS: readonly QuestDef[] = [
  { id: 'weekly-kill', giver: 'board', kind: 'weekly', town: 1, name: '이번 주의 토벌', goals: [{ type: 'kill', count: 1500 }], rewards: { protect: 1, gold: 2000 } },
  { id: 'weekly-raid', giver: 'raid', kind: 'weekly', town: 2, name: '레이드 참전', goals: [{ type: 'raid', count: 3 }], rewards: { protect: 1, gold: 2000 } },
  { id: 'weekly-enhance', giver: 'forge', kind: 'weekly', town: 1, name: '강화의 주', goals: [{ type: 'enhance', count: 10 }], rewards: { protect: 1, gold: 2000 } },
];
export const ALL_QUESTS: readonly QuestDef[] = [...STORY_QUESTS, ...DAILY_QUESTS, ...WEEKLY_QUESTS];

// ── 이정표(3개월 C3, 첫 30분·첫날·첫 주 새 목표 간격) ───────────────────────────────────────────
// 플레이타임 장부(work/playtime/ledger.json)는 P0 5분·P1 30분·P2 2시간마다 새 목표가 와야 한다. 스테이지 클리어·레벨·하드 클리어·전투방 정리는
// 이미 게임에 있는 일이지만 한 줄의 「다음 목표」로는 보이지 않는다. 이정표는 그 일들을 한 줄씩 보이게 하는 데이터다(퀘스트 창 「이정표」 줄,
// ui/questMilestoneModel.ts). 새 보상은 없다 — 보상 수치는 economy(C1) 소유. atMin은 C1 곡선 v3(장부 sim720-* 단위의 누적 진행 분)에 맞춘
// 설계 위치이고, 곡선이 바뀌면 아래 *_ANCHORS 표만 다시 맞춘다(게임은 순서만 쓴다).
export type MilestonePhase = 'P0' | 'P1' | 'P2' | 'P3';
export type MilestoneGoal =
  | { readonly type: 'level'; readonly level: number }
  /** 일반(r1-s01) 또는 하드(h1-s05) 스테이지 첫 클리어 */
  | { readonly type: 'clearStage'; readonly stage: string }
  /** 한 판 안의 전투방 정리 수(던전 장면에서만 값이 있고, 마을에서는 그 스테이지를 깼는지로 본다) */
  | { readonly type: 'room'; readonly stage: StageId; readonly count: number };
export interface MilestoneDef {
  readonly id: string;
  readonly phase: MilestonePhase;
  /** 장부 기준 누적 플레이 분(설계 위치) */
  readonly atMin: number;
  readonly name: string;
  readonly hint: string;
  readonly goal: MilestoneGoal;
}

type Anchors = readonly (readonly [number, number])[];
/** [서수, 누적 분]을 직선으로 이어 서수의 분을 구한다(표 밖은 끝값) */
function interp(a: Anchors, x: number): number {
  for (let i = 1; i < a.length; i++) {
    const [x1, y1] = a[i];
    if (x <= x1) { const [x0, y0] = a[i - 1]; return Math.round((y0 + ((y1 - y0) * (x - x0)) / (x1 - x0)) * 10) / 10; }
  }
  return a[a.length - 1][1];
}
/** 일반 스테이지 서수(1-1 = 1 … 6-10 = 60) → 누적 분. 장부 sim720 스테이지 단위(1-3 … 3-2, 3-10, 4-10, 5-1 … 6-7)에 맞추고 사이는 직선(1-1·1-2는 Lv2·Lv3 사이) */
export const STAGE_ANCHORS: Anchors = [[0, 3], [1, 11], [2, 23], [3, 34], [4, 58], [5, 76], [6, 85], [7, 105], [8, 131], [9, 156], [10, 191], [11, 200], [12, 208], [13, 244], [14, 270], [15, 289], [16, 326], [17, 354], [18, 371], [19, 415], [20, 439], [21, 448], [22, 467], [30, 756], [40, 1311], [41, 1319], [42, 1346], [43, 1417], [44, 1484], [45, 1600], [46, 1662], [47, 1820], [48, 1959], [49, 2168], [50, 2276], [55, 2888], [57, 3244], [60, 3700]];
/** 하드 스테이지 서수(하드 1-1 = 1 … 4-10 = 40) → 누적 분. 장부 sim720 하드 단위(1-1~1-10, 2-1~2-3, 2-10, 3-10, 4-2, 4-10) */
export const HARD_ANCHORS: Anchors = [[0, 210], [1, 216], [2, 225], [3, 234], [4, 252], [6, 279], [7, 297], [8, 307], [10, 396], [11, 458], [12, 477], [13, 491], [20, 773], [30, 1134], [32, 1338], [40, 1968]];
/** 레벨 → 누적 분. 장부 sim720 레벨 단위(Lv2~5·10·15…·74) 사이를 직선으로 잇는다 */
export const LEVEL_ANCHORS: Anchors = [[1, 0], [2, 13.4], [3, 21.1], [4, 30.2], [5, 47.6], [10, 94], [15, 165], [20, 260], [25, 362], [30, 487], [35, 640], [40, 808], [45, 1005], [50, 1185], [55, 1374], [60, 1654], [65, 2160], [70, 2723], [71, 2878], [72, 2983], [73, 3096], [74, 3228], [75, 3340]];
/** 레벨 이정표 범위: Lv2(첫 레벨업) ~ Lv75(장부 첫 주 끝). 그 위는 C1이 환생 구간 단위를 추가할 때 늘린다 */
export const LEVEL_CARD_RANGE = [2, 75] as const;

// 장부 구간: P0 첫 30분 · P1 첫날(8h) · P2 첫 주(56h = 3360분) · P3 첫 달(지역6 후반 6-8~6-10이 여기 든다)
const phaseOf = (min: number): MilestonePhase => (min <= 30 ? 'P0' : min <= 480 ? 'P1' : min <= 3360 ? 'P2' : 'P3');
const stageKey = (r: number, i: number): string => `r${r}-s${String(i).padStart(2, '0')}`;

function stageMilestones(): MilestoneDef[] {
  const out: MilestoneDef[] = [];
  for (const r of REGIONS_90D) {
    if (!r.legacyId) continue; // 지도가 없는 지역(5·6)은 C2가 지도를 붙이면(legacyId) 이정표가 자동으로 따라온다
    for (let i = 1; i <= 10; i++) {
      const ord = (r.no - 1) * 10 + i;
      const atMin = interp(STAGE_ANCHORS, ord);
      const boss = i === 5 ? r.midBoss : i === 10 ? r.finalBoss : '';
      out.push({
        id: `ms-stage-${r.no}-${i}`, phase: phaseOf(atMin), atMin,
        name: boss ? `${r.no}-${i} · ${boss} 격파` : `${r.no}-${i} · 클리어`,
        hint: boss ? `${r.name}의 ${i === 5 ? '중간보스' : '지역보스'}를 쓰러뜨려요` : `${r.name} ${r.no}-${i}을 처음 깨요 · 다음 스테이지가 열려요`,
        goal: { type: 'clearStage', stage: stageKey(r.no, i) },
      });
    }
  }
  return out;
}
/** 하드 모드는 스테이지 5·10(중간·지역보스)만 이정표로 둔다 */
function hardMilestones(): MilestoneDef[] {
  const out: MilestoneDef[] = [];
  for (const [r, i] of [[1, 5], [1, 10], [2, 5], [2, 10], [3, 5], [3, 10], [4, 5], [4, 10]] as const) {
    const reg = REGIONS_90D[r - 1], boss = i === 5 ? reg.midBoss : reg.finalBoss, atMin = interp(HARD_ANCHORS, (r - 1) * 10 + i);
    out.push({ id: `ms-hard-${r}-${i}`, phase: phaseOf(atMin), atMin, name: `하드 ${r}-${i} · ${boss} 격파`, hint: `하드 난이도의 ${reg.name} ${i === 5 ? '중간보스' : '지역보스'}예요`, goal: { type: 'clearStage', stage: `h${r}-s${String(i).padStart(2, '0')}` } });
  }
  return out;
}
function levelMilestones(): MilestoneDef[] {
  const out: MilestoneDef[] = [];
  for (let lv = LEVEL_CARD_RANGE[0]; lv <= LEVEL_CARD_RANGE[1]; lv++) {
    const atMin = interp(LEVEL_ANCHORS, lv);
    out.push({ id: `ms-lv-${lv}`, phase: phaseOf(atMin), atMin, name: `레벨 ${lv} 도달`, hint: '사냥으로 경험치를 모아요 · 레벨업마다 스킬 포인트를 얻어요', goal: { type: 'level', level: lv } });
  }
  return out;
}
/**
 * 첫 세 스테이지(1-1~1-3)의 전투방 이정표: 한 판 = 전투방 3 + 마지막 아레나(stages.ts). 진행 중에는 던전 HUD의 방 목표 패널(hud.ts: 방 이름·목표 문구·n/m)이
 * 보여 주고(정식 호스트 실입력 표본 qa/3month/c3/desktop/12-play-g1.webp), 퀘스트 창 줄은 들어가기 전 안내(문까지 자동 이동)다 — 창은 마을에서만 열리므로
 * 던전 안 갱신은 HUD가 맡는다. 그 스테이지를 깬 뒤에는 방 이정표가 모두 이뤄진 것으로 본다(milestoneStatus). [스테이지, 방 수, 누적 분]
 */
const ROOM_AT: readonly (readonly [number, number, number])[] = [
  [1, 1, 4.5], [1, 2, 7.5], [1, 3, 10],
  [2, 1, 15.5], [2, 2, 18], [2, 3, 20.5],
  [3, 1, 25], [3, 2, 27.5], [3, 3, 29.5],
];
const ROOM_LABEL = ['첫', '두 번째', '세 번째'] as const;
function roomMilestones(): MilestoneDef[] {
  return ROOM_AT.map(([st, n, atMin]) => ({ id: `ms-room-1-${st}-${n}`, phase: 'P0' as const, atMin, name: `1-${st} · ${ROOM_LABEL[n - 1]} 전투방 정리`, hint: '방 안의 몬스터를 모두 잡으면 다음 문이 열려요', goal: { type: 'room', stage: stageKey(1, st) as StageId, count: n } }));
}

export const MILESTONES: readonly MilestoneDef[] = [...roomMilestones(), ...stageMilestones(), ...hardMilestones(), ...levelMilestones()].sort((a, b) => a.atMin - b.atMin || a.id.localeCompare(b.id));
